import React, { useState, useCallback } from 'react';
import { Card, Button, Flex, Typography, theme, Modal, Dropdown, notification } from 'antd';
import { EditOutlined, DeleteOutlined, CopyOutlined, MoreOutlined, CodeOutlined } from '@ant-design/icons';
import { CustomIntegration } from '../CustomIntegration/CustomIntegration';

import { createLogger } from '../../../../../../utils/logger';
const logger = createLogger('features-assistants');

const { useToken } = theme;
const { Text } = Typography;

interface CardCustomIntegrationActionProps {
  message: string;
  onDelete: () => void;
  onDuplicate: () => void;
  onEdit: (newMessage: string) => void;
  chatbotId?: string;
  flowId?: string;
}

export const CardCustomIntegrationAction: React.FC<CardCustomIntegrationActionProps> = ({
  message,
  onDelete,
  onDuplicate,
  onEdit,
  chatbotId,
  flowId
}) => {
  const { token } = useToken();
  const [showEditModal, setShowEditModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Extraer el ID de la función del mensaje
  const getFunctionId = (msg: string | any) => {
    // Handle case where message might be an object with a message property
    const messageStr = typeof msg === 'string' ? msg : msg?.message || '';
    
    if (!messageStr || typeof messageStr !== 'string') {
      logger.warn('getFunctionId: Invalid message format, expected string', msg);
      return '';
    }
    
    const match = messageStr.match(/@[^/]+\/([^{]+)/);
    return match ? match[1] : '';
  };
  
  // Extraer el nombre de la función del mensaje
  const getFunctionName = (msg: string | any) => {
    // Handle case where message might be an object with a message property
    const messageStr = typeof msg === 'string' ? msg : msg?.message || '';
    
    if (!messageStr || typeof messageStr !== 'string') {
      logger.warn('getFunctionName: Invalid message format, expected string', msg);
      return 'Custom Integration';
    }
    
    try {
      const match = messageStr.match(/\{.*\}/);
      if (match) {
        const functionInfo = JSON.parse(match[0]);
        if (functionInfo.name) {
          return functionInfo.name;
        }
      }
    } catch (error) {
      logger.error('Error parsing function name:', error);
    }

    // Si no se puede extraer el nombre, usar el ID formateado como fallback
    const functionId = getFunctionId(messageStr);
    if (!functionId) {
      return 'Custom Integration';
    }
    return functionId
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };
  
  // Extraer los parámetros del mensaje
  const extractParams = (msg: string | any) => {
    // Handle case where message might be an object with a message property
    const messageStr = typeof msg === 'string' ? msg : msg?.message || '';
    
    if (!messageStr || typeof messageStr !== 'string') {
      return {};
    }
    
    try {
      const match = messageStr.match(/\{.*\}/);
      if (match) {
        const functionInfo = JSON.parse(match[0]);
        return functionInfo.parameters || {};
      }
      return {};
    } catch (error) {
      logger.error('Error parsing message parameters:', error);
      return {};
    }
  };
  
  // Extraer la información completa de la integración
  const extractIntegrationInfo = (msg: string | any) => {
    // Handle case where message might be an object with a message property
    const messageStr = typeof msg === 'string' ? msg : msg?.message || '';
    
    if (!messageStr || typeof messageStr !== 'string') {
      return {};
    }
    
    try {
      const match = messageStr.match(/\{.*\}/);
      if (match) {
        return JSON.parse(match[0]);
      }
      return {};
    } catch (error) {
      logger.error('Error parsing integration info:', error);
      return {};
    }
  };
  
  const functionId = getFunctionId(message);
  const functionName = getFunctionName(message);
  const params = extractParams(message);
  const integrationInfo = extractIntegrationInfo(message);
  
  // Preparar los datos de la función para el formulario
  const prepareFunctionData = useCallback(() => {
    return {
      id: functionId,
      title: functionName,
      name: functionName,
      description: integrationInfo.description || `Custom function: ${functionName}`,
      parameters: {
        properties: Object.entries(params).reduce((acc: any, [key, value]) => {
          acc[key] = {
            description: key,
            type: typeof value === 'number' ? 'number' : 'string',
            form: true,
            value: value
          };
          return acc;
        }, {}),
        required: []
      },
      integration: {
        name: 'Custom Integration',
        id: functionId,
        logo: 'https://placehold.co/200x100/007bff/ffffff?text=Custom'
      },
      code: integrationInfo.code || '',
      type: 'custom'
    };
  }, [functionId, functionName, integrationInfo, params]);
  
  const handleEditClick = () => {
    setShowEditModal(true);
  };
  
  const handleFormSave = (newMessage: string) => {
    onEdit(newMessage);
    setShowEditModal(false);
  };
  
  const handleFormCancel = () => {
    setShowEditModal(false);
  };

  const handleDeleteClick = async () => {
    setIsDeleting(true);
    try {
      // Para integraciones personalizadas, solo eliminar el mensaje del flujo
      onDelete();
      notification.success({
        message: 'Success',
        description: 'Custom integration removed from flow'
      });
    } catch (error: any) {
      logger.error('Error in delete process:', error);
      notification.error({
        message: 'Error',
        description: error?.message || 'Failed to remove custom integration'
      });
    } finally {
      setIsDeleting(false);
    }
  };
  
  const menuItems = [
    {
      key: 'edit',
      label: 'Edit',
      icon: <EditOutlined />,
      onClick: handleEditClick
    },
    {
      key: 'duplicate',
      label: 'Duplicate',
      icon: <CopyOutlined />,
      onClick: onDuplicate
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: <DeleteOutlined />,
      onClick: handleDeleteClick,
      disabled: isDeleting
    }
  ];
  
  return (
    <>
      <Card 
        style={{ 
          maxWidth: '400px',
          width: '100%',
          borderRadius: token.borderRadiusLG,
          boxShadow: token.boxShadowTertiary,
          cursor: 'pointer'
        }}
        bodyStyle={{ padding: token.paddingSM }}
        onClick={handleEditClick}
      >
        <Flex align="center" justify="space-between" style={{ width: '100%' }}>
          <Flex gap={token.marginMD} align="center">
            <div 
              style={{ 
                width: 120,
                height: 70,
                backgroundColor: '#007bff',
                borderRadius: token.borderRadiusLG,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '12px',
                fontWeight: 'bold',
                textAlign: 'center',
                padding: '8px'
              }}
            >
              <CodeOutlined style={{ fontSize: '24px', marginRight: '8px' }} />
              Custom
            </div>
            <Flex vertical gap={token.marginXS}>
              <Text strong style={{ fontSize: '16px', lineHeight: '24px', margin: 0 }}>
                Custom Integration
              </Text>
              <Text type="secondary" style={{ fontSize: '14px', lineHeight: '20px', margin: 0 }}>
                {functionName}
              </Text>
            </Flex>
          </Flex>
          <div onClick={(e) => e.stopPropagation()}>
            <Dropdown
              menu={{ items: menuItems }}
              trigger={['click']}
              placement="bottomRight"
            >
              <Button 
                icon={<MoreOutlined />} 
                size="small"
                type="text"
                loading={isDeleting}
              />
            </Dropdown>
          </div>
        </Flex>
      </Card>
      
      <Modal
        title={`Edit Custom Integration: ${functionName}`}
        open={showEditModal}
        onCancel={handleFormCancel}
        footer={null}
        width={700}
        destroyOnClose={true}
      >
        <CustomIntegration
          onBack={handleFormCancel}
          onSave={handleFormSave}
          chatbotId={chatbotId}
        />
      </Modal>
    </>
  );
}; 